/**
 * Usage over time — one metric at a time.
 *
 * The old chart stacked pages, documents and requests on a single axis, where
 * pages run two orders of magnitude above the other two: documents and
 * requests were flat lines along the baseline and the chart could only ever be
 * read as "pages". One series on one axis, chosen by the tiles above, shows
 * each metric at its own scale.
 */
import {
  Bar, BarChart, CartesianGrid, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts'
import { Receipt } from 'lucide-react'
import { Card, EmptyState } from './BillingPrimitives'
import { METRICS } from './MetricTiles'
import { formatCompact, formatInt } from '../../utils/billing'
import { isAllTimeLabel } from './useBillingData'

export function ChartTooltip({ active, payload, label, chart, metric, allowance, grouping }) {
  if (!active || !payload?.length) return null
  const value = payload[0].value || 0
  return (
    <div
      className="rounded-lg px-3 py-2 text-xs shadow-lg"
      style={{ background: chart.tooltipBg, border: `1px solid ${chart.tooltipBorder}`, color: chart.tooltipText }}
    >
      <p className="font-semibold mb-1">{label}</p>
      <p className="flex items-center gap-2 tabular-nums">
        <i className="w-2 h-2 rounded-sm" style={{ background: payload[0].fill }} />
        {metric.label}
        <span className="ml-auto font-semibold">{formatInt(value)}</span>
      </p>
      {/* The allowance is a monthly figure: a week's pages as a share of it
          would read as a fraction of the month, not of any allowance. */}
      {allowance > 0 && metric.key === 'pages_processed' && grouping !== 'weekly' && (
        <p className="mt-1 pt-1 border-t text-[11px] opacity-70" style={{ borderColor: chart.tooltipBorder }}>
          {Math.round((value / allowance) * 100)}% of a {formatCompact(allowance)}-page allowance
        </p>
      )}
    </div>
  )
}

export default function UsageTrend({ data, metricKey, periodLabel, loading, chart, allowance, grouping }) {
  const metric = METRICS.find(m => m.key === metricKey) || METRICS[0]
  const color = chart.series[metric.seriesIndex]

  // Recharts silently discards a reference line that falls outside the y
  // domain, and the domain is scaled to the data. An allowance far above
  // actual usage would leave the caption promising a dashed line that is not
  // drawn — and forcing the domain up to meet it would flatten every bar into
  // the bottom of the plot. Above that ratio the allowance is not the story,
  // so the line and its caption both go.
  const dataMax = Math.max(0, ...data.map(d => Number(d[metric.key]) || 0))
  const showAllowance =
    metric.key === 'pages_processed' && grouping !== 'weekly' && allowance > 0 && allowance <= dataMax * 1.25

  return (
    <Card className="p-5 animate-card-in" style={{ animationDelay: '320ms' }}>
      <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
        <div>
          <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
            {metric.label} over time
          </h2>
          {/* No range control here: the page's Analysis period governs every
              panel, and a second control for the same state is a trap. */}
          <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
            {isAllTimeLabel(periodLabel) ? 'All time' : `Last ${periodLabel}`} · {grouping === 'weekly' ? 'grouped by week' : 'grouped by calendar month'}
            {showAllowance && ` · dashed line marks your ${formatInt(allowance)}-page allowance`}
          </p>
        </div>
        <span className="text-[11px] text-gray-400 dark:text-gray-500">
          Click a tile above to change the metric
        </span>
      </div>

      <div className={`transition-opacity duration-200 ${loading ? 'opacity-40' : 'opacity-100'}`}>
        {data.length > 0 ? (
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={data} margin={{ top: 12, right: 8, left: 0, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={chart.grid} vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 11, fill: chart.axis }} tickLine={false} axisLine={{ stroke: chart.grid }} />
              <YAxis
                tick={{ fontSize: 11, fill: chart.axis }}
                tickLine={false}
                axisLine={false}
                width={48}
                tickFormatter={formatCompact}
              />
              <Tooltip
                cursor={{ fill: chart.grid, opacity: 0.35 }}
                content={<ChartTooltip chart={chart} metric={metric} allowance={allowance} grouping={grouping} />}
              />
              {showAllowance && (
                <ReferenceLine
                  y={allowance}
                  stroke={chart.axis}
                  strokeDasharray="4 4"
                  label={{
                    value: 'Allowance',
                    position: 'insideTopRight',
                    fill: chart.axis,
                    fontSize: 10,
                  }}
                />
              )}
              <Bar
                dataKey={metric.key}
                name={metric.label}
                fill={color}
                radius={[4, 4, 0, 0]}
                maxBarSize={56}
                animationDuration={600}
                animationEasing="ease-out"
              />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <EmptyState
            icon={Receipt}
            title="No usage in this period"
            body="Pick a longer range, or process a document to see it appear here."
          />
        )}
      </div>
    </Card>
  )
}
